/**
 * Authentication and authorization types
 */

import { BaseEntity, User } from '../common';
import type { ClientType } from './audit';
import type { Permission as PermissionString } from './permissions';
import type { Role } from './permissions';

export { AuditEventType } from './audit';
export type { AuditEvent, ClientType } from './audit';

export { MfaMethod } from './mfa';
export type { UserMfa, MfaChallenge } from './mfa';

export type { MobileAuthStorage } from './mobileAuth';
export {
  Role,
  ROLE_PERMISSIONS,
  ROLE_HIERARCHY,
  hasPermission,
  roleSatisfiesMinimum,
  isPermissionString,
} from './permissions';
export type { Permission as PermissionAction } from './permissions';

// Authentication request/response types
export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
  /**
   * API-1659 — client-resolved `prefers-color-scheme` used only to write
   * `loci-theme` when the account setting is `system`. Not persisted.
   */
  resolvedTheme?: 'light' | 'dark';
}

export interface LoginResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface RegisterRequest {
  email: string;
  password: string;
  confirmPassword: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  acceptTerms: boolean;
}

export interface RegisterResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
  confirmPassword: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface VerifyEmailRequest {
  token: string;
}

// Session types (canonical identity model — maps to Prisma Session)
export interface Session extends BaseEntity {
  userId: string;
  refreshTokenFamily: string;
  deviceInfo?: Record<string, unknown> | null;
  clientType: ClientType;
  issuedAt: Date;
  lastActiveAt: Date;
  expiresAt: Date;
  revokedAt?: Date | null;
  revokeReason?: string | null;
}

// Refresh token types (maps to Prisma RefreshToken)
export interface RefreshToken extends BaseEntity {
  sessionId: string;
  tokenHash: string;
  familyId: string;
  generation: number;
  issuedAt: Date;
  expiresAt: Date;
  usedAt?: Date | null;
  replacedByTokenId?: string | null;
}

// JWT payload types
export interface JwtPayload {
  sub: string; // user id
  email: string;
  iat: number;
  exp: number;
  type: 'access' | 'refresh';
}

// Password reset types
export interface PasswordResetToken extends BaseEntity {
  userId: string;
  token: string;
  expiresAt: Date;
  used: boolean;
}

// Email verification types
export interface EmailVerificationToken extends BaseEntity {
  userId: string;
  email: string;
  token: string;
  expiresAt: Date;
  verified: boolean;
}

// OAuth types
export interface OAuthProvider {
  id: string;
  name: string;
  clientId: string;
  enabled: boolean;
}

export interface OAuthAccount extends BaseEntity {
  userId: string;
  provider: string;
  providerAccountId: string;
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: Date;
  scope?: string;
}

// Social login request/response
export interface SocialLoginRequest {
  provider: 'google' | 'facebook' | 'apple' | 'github';
  accessToken: string;
  idToken?: string;
}

// Permission and role types
export interface Permission {
  id: string;
  name: string;
  description?: string;
  resource: string;
  action: string;
}

export interface RoleDefinition extends BaseEntity {
  name: string;
  description?: string;
  permissions: Permission[];
  isDefault: boolean;
}

export interface UserRole extends BaseEntity {
  userId: string;
  roleId: string;
  assignedBy: string;
  assignedAt: Date;
  expiresAt?: Date;
}

// Authentication context types (React provider — renamed to avoid collision with server-side AuthContext)
export interface ReactAuthContext {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (data: ResetPasswordRequest) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
}

/**
 * Server-side authorization context populated by `authenticateToken` middleware.
 * Available on `req.user` after authentication succeeds.
 *
 * - `role` is extracted from the JWT claim (defaults to USER for legacy tokens).
 * - `permissions` are resolved from the RBAC permission matrix — not stored in the JWT.
 * - `id` is a backward-compatible alias for `userId`.
 */
export interface AuthContext {
  userId: string;
  email: string;
  sessionId?: string;
  role: Role;
  permissions: readonly PermissionString[];
  /** Backward-compatible alias for `userId`. */
  id: string;
  /**
   * WEB-39 — advertiser scope from the JWT claim / User.advertiserId.
   * Present when the user is linked to an Advertiser org for the self-serve
   * `/advertise` portal. Absent for consumer-only accounts.
   */
  advertiserId?: string;
}

// Auth error types
export type AuthErrorCode =
  | 'INVALID_CREDENTIALS'
  | 'EMAIL_NOT_VERIFIED'
  | 'ACCOUNT_LOCKED'
  | 'TOKEN_EXPIRED'
  | 'TOKEN_INVALID'
  | 'USER_NOT_FOUND'
  | 'EMAIL_ALREADY_EXISTS'
  | 'WEAK_PASSWORD'
  | 'RATE_LIMITED'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN';

export interface AuthError {
  code: AuthErrorCode;
  message: string;
  details?: Record<string, any>;
}
